// 📬 MessagesInboxButton
//
// Header entry (sibling of the NotificationsBell). Clicking it OPENS a
// dropdown preview of the latest conversations (mirroring the bell), it
// does NOT jump straight to the inbox. Clicking a row deep-links into
// /messages?thread=<id>. A footer link goes to the full inbox.
//
// ⚠️ WHY A ROW IS NOT JUST A NAME.
//
// Reported as "messages from the same person appearing multiple times":
//
//     seed-cust-paid  1d  A dispute has been opened on this work…
//     seed-cust-paid  1d  A dispute has been opened on this work…
//     seed-cust-paid  1d  A dispute has been opened on this work…
//
// Those are three DIFFERENT threads, not one thread rendered three times —
// a customer has one thread per review job, so the same counterpart
// legitimately owns several. The bug was that the row identified the
// PERSON and nothing else, while the thing that tells the threads apart —
// `thread.title` ("Review c0e82461") — was already in the payload and
// thrown away. `counterpartLabel` falls back to the title only when a
// thread has no counterpart at all, so on every real thread it was dead
// code. Three identical rows is what "who" looks like without "about
// what": the reader cannot tell them apart OR pick the right one.
//
// A row now carries the four things a person scanning an inbox needs:
// WHO (monogram + name), WHAT IT IS ABOUT (subject line + kind icon),
// WHAT WAS SAID (preview, prefixed "You:" when the last message is
// theirs), and WHEN. Unread is carried by weight and a dot.
//
// WHO shows the person's picture when we have one, and a deterministic
// monogram when we do not — the slot is the same size either way, so the
// list does not reflow as pictures resolve. Pictures arrive on the thread
// itself (`participantPhotos`, alongside `participantNames`): the thread
// is what establishes the right to see them, exactly as it does for the
// name. A group thread keeps the monogram — one of three faces is not a
// picture of the conversation.
//
// The live unread badge stays sourced from communications.chatUnread()
// and is kept fresh three ways:
//   1. socket `CHAT_MESSAGE` events (instant),
//   2. a route change to/from /messages (reflects reads),
//   3. a slow interval fallback for when the socket is unavailable.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { communications } from '../../api/communications.js';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { useSocketEvent } from '../../context/SocketContext.jsx';
import { counterpartIds, primeFromThreads, useUserNames } from '../../modules/communications/useUserNames.js';
import { storage } from '../../utils/storage.js';
import { Icon } from '../elements/icon.element.jsx';
import { PersonAvatar } from '../elements/person-avatar.element.jsx';
import { realNamesIn } from '@moksy/common/src/identity/displayName.js';

import { Button } from '../elements/Button.jsx';
const REFRESH_MS = 30000;
const BRAND = 'var(--moksy-brand)';

// A thread's subject line, and the glyph for what kind of thing it is.
// `title` is set by whoever opened the thread ("Review c0e82461"); the
// fallbacks keep a row from ever showing a bare, unlabelled entry.
const SUBJECT_ICONS = {
  review_job: 'reviewer',
  project:    'projects',
  dispute:    'warning',
  support:    'help',
};

const subjectOf = (t) => {
  const title = String(t?.title || '').trim();
  if (title) return title;
  const kind = String(t?.subjectType || '').replace(/_/g, ' ').trim();
  return kind ? kind.replace(/^./, (c) => c.toUpperCase()) : '';
};

// Monogram tints. Six low-chroma pairs that sit beside the brand orange
// without competing with the unread dot, which is the only element in the
// row allowed to be brand-coloured.
const MONOGRAM_TINTS = [
  'bg-slate-100 text-slate-600',
  'bg-sky-100 text-sky-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-violet-100 text-violet-700',
  'bg-rose-100 text-rose-700',
];

// Stable per-counterpart colour: the same person is the same colour on
// every render and every reload, so the list can be scanned by colour.
// Keyed on the thread's counterpart label rather than a random index.
const tintFor = (key) => {
  const s = String(key || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return MONOGRAM_TINTS[hash % MONOGRAM_TINTS.length];
};

// First letter of each of the first two words — "Ada Lovelace" → "AL",
// "seed-cust-paid" → "SC".
//
// Only words STARTING with a letter or digit count. A name that could not
// be resolved to a profile falls back to a shortened id, and those carry an
// ellipsis ("aspel-…lder"), which was being taken as a word and rendered
// the monogram "A…" — a broken-looking glyph in the one spot whose whole
// job is to be recognisable. Falls back to a dash rather than an empty
// circle so a still-resolving name is not a hole in the row.
const initialsOf = (label) => {
  // Only the parts of the label that are somebody's name. "Unknown
  // participant" is not a person called U. P., and on a mixed group row the
  // letter worth showing belongs to the person who is really there.
  const words = realNamesIn(label).join(' ')
    .trim()
    .split(/[\s._\-@]+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w));
  if (!words.length) return '—';
  return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
};

// The counterpart's picture, or '' — from the thread, which is the only
// source that has one: the public-profile endpoint filters on
// settings.privacy.profilePublic and so answers for almost nobody.
//
// Only for a 1:1 thread. With several people on the other side there is no
// single face to show, and picking the first participant's would quietly
// misattribute the conversation.
const photoOf = (thread, myUserId) => {
  const ids = counterpartIds(thread, myUserId);
  if (ids.length !== 1) return '';
  return String((thread?.participantPhotos || {})[ids[0]] || '');
};

// WHO, in one slot. PersonAvatar owns the picture and what happens when it
// fails to load; the monogram below is this surface's own fallback — the
// tint is a hash of the name, so the same person is the same colour every
// time and the list can be scanned by colour before it is read.
const RowAvatar = ({ src, initials, tint, name }) => (
  <PersonAvatar
    src={src}
    className="mt-0.5 h-9 w-9"
    title={name}
    fallback={(
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${tint}`}
        aria-hidden="true"
        title={name}
      >
        {initials}
      </span>
    )}
  />
);

// Compact relative time ("now", "5m", "3h", "2d", else a short date).
const fmtRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const MessagesInboxButton = () => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const { user } = useAuthContext();
  const myUserId = String(user?.userId || user?.id || '');
  const { counterpartLabel } = useUserNames();

  const refresh = useCallback(async () => {
    if (!storage.hasUsableAccessToken()) {
      setUnread(0);
      return;
    }
    try {
      const data = await communications.chatUnread();
      setUnread(Number(data?.total || 0));
    } catch {
      // Soft-fail: comms can blip. Leave the last known count in place.
    }
  }, []);

  // Pull the latest conversations for the preview list (top 5 by recency).
  const loadThreads = useCallback(async () => {
    if (!storage.hasUsableAccessToken()) return;
    setLoading(true);
    try {
      const data = await communications.listThreads();
      const list = Array.isArray(data) ? data : (data?.threads || []);
      const sorted = [...list].sort(
        (a, b) => new Date(b?.lastMessageAt || 0).getTime() - new Date(a?.lastMessageAt || 0).getTime()
      );
      const top = sorted.slice(0, 5);
      // Names the server already resolved, into the shared cache — so the
      // reading pane and the roster on /messages say the same thing about
      // the same person as this dropdown does.
      primeFromThreads(top);
      setThreads(top);
    } catch {
      // Soft-fail: keep whatever is already shown.
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + slow-poll fallback.
  useEffect(() => {
    refresh();
    const timer = setInterval(() => {
      if (!document.hidden) refresh();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  // Re-sync whenever we land on (or leave) the inbox — reading a thread
  // there drops the unread total.
  useEffect(() => { refresh(); }, [location.pathname, refresh]);

  // Instant bump on inbound chat messages — and refresh the open preview.
  useSocketEvent((payload) => {
    if (payload?.event === 'CHAT_MESSAGE') {
      refresh();
      if (open) loadThreads();
    }
  });

  // Fetch the preview each time the dropdown opens.
  useEffect(() => {
    if (open) loadThreads();
  }, [open, loadThreads]);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openThread = (id) => {
    setOpen(false);
    if (id) navigate(`/messages?thread=${id}`);
  };

  const rows = useMemo(() => threads.map((t) => {
    const label = counterpartLabel(t, myUserId);
    // Who spoke last. Without this, your own message reads back as though
    // the other person sent it — which matters most on exactly the rows
    // where you are waiting for a reply.
    const mine = String(t.lastMessageSenderId || '') === myUserId;
    const fromSystem = String(t.lastMessageSenderId || '') === 'system';
    return {
      id: t.id || t._id,
      label,
      initials: initialsOf(label),
      photo: photoOf(t, myUserId),
      tint: tintFor(label),
      // The line that tells otherwise-identical rows apart.
      subject: subjectOf(t),
      subjectIcon: SUBJECT_ICONS[t.subjectType] || 'conversation',
      preview: t.lastMessagePreview || 'No messages yet',
      prefix: mine ? 'You: ' : '',
      fromSystem,
      when: fmtRelative(t.lastMessageAt),
      unread: Number(t.unreadCount || 0) > 0
    };
  }), [threads, counterpartLabel, myUserId]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="relative p-2 rounded-full hover:bg-gray-100"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={unread > 0 ? `Messages (${unread} unread)` : 'Messages'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5m-9 6l3.5-2.5A2 2 0 0 1 10.7 17H17a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7a3 3 0 0 0-3 3v13z" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-background border border-gray-200 rounded-2xl shadow-lg z-50">
          {/* Padding lives on the header and footer, not the whole panel, so
              the scrolling list can run edge to edge and its hover states
              read as full-width rows rather than floating pills. */}
          <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Messages
              {unread > 0 && (
                <span
                  className="inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-4 text-white"
                  style={{ backgroundColor: BRAND }}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </span>
            {/* Outside-click and Escape already close this, but neither is
                visible. A panel that covers the page needs a control you can
                see and aim at. */}
            <Button variant="ghost"
              onClick={() => setOpen(false)}
              aria-label="Close messages">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </Button>
          </div>

          <div className="max-h-[22rem] overflow-y-auto border-t border-gray-100">
            {loading && threads.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-gray-400">
                <Icon name="clock" className="h-5 w-5" />
                <span className="text-xs">Loading conversations…</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-gray-400">
                <Icon name="conversation" className="h-6 w-6" />
                <span className="text-xs">No conversations yet.</span>
                <span className="text-[10px] leading-4 text-gray-400">
                  Messages about your reviews and projects land here.
                </span>
              </div>
            ) : rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => openThread(r.id)}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                  r.unread ? 'bg-primary/[0.03]' : ''
                }`}
              >
                {/* WHO — picture when we have one, monogram when we do
                    not. Decorative either way: the name is right there in
                    text, so announcing it twice only slows a screen reader
                    down. */}
                <RowAvatar
                  src={r.photo}
                  initials={r.initials}
                  tint={r.tint}
                  name={r.label}
                />

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className={`truncate text-sm leading-5 ${r.unread ? 'font-semibold text-neutral' : 'font-medium text-gray-800'}`}>
                      {r.label}
                    </span>
                    <span className="shrink-0 text-[10px] leading-4 text-gray-400">{r.when}</span>
                  </span>

                  {/* WHAT IT IS ABOUT — the line that was missing, and the
                      only thing distinguishing several threads with the
                      same counterpart. */}
                  {r.subject && (
                    <span className="mt-0.5 flex items-center gap-1 text-[11px] leading-4 text-gray-500">
                      <Icon name={r.subjectIcon} className="h-3 w-3 shrink-0" />
                      <span className="truncate">{r.subject}</span>
                    </span>
                  )}

                  <span className={`mt-1 flex items-center gap-1.5 text-xs leading-5 ${r.unread ? 'text-gray-700' : 'text-gray-500'}`}>
                    {/* Attribution sits on the preview, NOT on the monogram.
                        The monogram answers "who is this conversation with",
                        which does not change when the platform posts a
                        status message into it — an avatar that swapped to a
                        system glyph on those threads destroyed the one thing
                        it is for: being the same mark for the same person
                        every time you scan the list. */}
                    {r.fromSystem && (
                      <Icon name="shield" className="h-3 w-3 shrink-0 text-gray-400" title="From Moksy" />
                    )}
                    <span className="truncate">
                      {r.prefix && <span className="text-gray-400">{r.prefix}</span>}
                      {r.preview}
                    </span>
                    {r.unread && (
                      <span
                        className="ml-auto h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: BRAND }}
                        aria-label="Unread"
                      />
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="border-t border-gray-100 px-4 py-2.5 text-right">
            <Button variant="link" size="bare" onClick={() => { setOpen(false); navigate('/messages'); }} rightIcon={<Icon name="arrowRight" className="h-3 w-3" />}>
              Open Inbox
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
